/**
 * PrintingGraphs component
 * Displays 6 interactive graphs for print-photo event analysis
 *
 * Graphs:
 * 1. Z Position Over Time - Blue line showing Z axis progression
 * 2. E Positions - 6 lines tracking extruder positions and differences
 * 3. Deviation Percentage - Red line showing deviation over time
 * 4. Temperature - Dual lines for T0 and T1 temperatures
 * 5. G-Code Offset - Yellow/red lines showing manual/auto mode transitions
 * 6. Report Data - Interactive 3-line chart parsed from report CSV
 *
 * Features:
 * - Shared activeEventIndex synchronized across all graphs
 * - Green ReferenceLine showing active event on all charts
 * - Click handlers to update active event
 * - Interactive zoom on report data chart
 * - Responsive layout (6 cols md, 12 cols xs)
 */

import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Grid, Card, CardContent, styled } from '@mui/material';
import { Event } from '../../types';
import { getFeederInfo, FeederInfo } from './PrintingGraphs.utils';

interface ReportRow {
  index: number;
  cncE: string;
  gcodePosition: string;
  feederP: string;
}

interface ReportData {
  rows: ReportRow[];
  min: number;
  max: number;
}

interface ReportChartInfo {
  domainXMin: number;
  domainXMax: number;
  domainYMin: number;
  domainYMax: number;
  rows: ReportRow[];
  step: number;
}

const StyledCard = styled(Card)({
  borderRadius: 0,
  boxShadow: 'none',
  border: '1px solid #ccc',
});

export interface PrintingGraphsProps {
  events: Event[];
  reportdata?: ReportData;
  activeevent: number;
  onActiveIndexChanged?: (index: number) => void;
}

export const PrintingGraphs = ({
  events,
  reportdata,
  activeevent,
  onActiveIndexChanged,
}: PrintingGraphsProps) => {
  const feederInfo = getFeederInfo(events);

  let lastReportStartEvent = 0;

  const [reportChartInfo, setReportChartInfo] = useState<ReportChartInfo>({
    domainXMin: 0,
    domainXMax: (reportdata?.rows?.length ?? 1) - 1,
    domainYMin: reportdata?.min ?? 0,
    domainYMax: reportdata?.max ?? 0,
    rows: reportdata?.rows ?? [],
    step: Math.ceil(((reportdata?.rows?.length ?? 1) - 1) / 16),
  });

  if (activeevent === -1 || !feederInfo[activeevent]) return null;

  const currentEvent = feederInfo[activeevent];

  const {
    index,
    temperature,
    temperature2,
    gcodePosition,
    cncE,
    z,
    feederE,
    feederP,
    cncEvsFeederP,
    devPercentage,
    difGcodePositionFeederP,
    gCodeOffset,
    klipperGCodeOffset,
  } = currentEvent;

  const mouseDownHandler = (event: unknown) => {
    lastReportStartEvent = (event as { activeTooltipIndex?: number })?.activeTooltipIndex ?? 0;
  };

  const mouseUpHandler = (event: unknown) => {
    const endIndex = (event as { activeTooltipIndex?: number })?.activeTooltipIndex ?? 0;
    if (Math.abs(endIndex - lastReportStartEvent) < 10) {
      setReportChartInfo({
        domainXMin: 0,
        domainXMax: (reportdata?.rows?.length ?? 1) - 1,
        domainYMin: reportdata?.min ?? 0,
        domainYMax: reportdata?.max ?? 0,
        rows: reportdata?.rows ?? [],
        step: Math.ceil(((reportdata?.rows?.length ?? 1) - 1) / 16),
      });
      return;
    }
    let firstIndex;
    let lastIndex;
    if (endIndex > lastReportStartEvent) {
      firstIndex = lastReportStartEvent;
      lastIndex = endIndex;
    } else {
      firstIndex = endIndex;
      lastIndex = lastReportStartEvent;
    }
    const firstEvent = reportdata?.rows[firstIndex];
    const lastEvent = reportdata?.rows[lastIndex];
    if (!firstEvent || !lastEvent) return;

    const yMin = Math.min(
      parseFloat(firstEvent.feederP),
      parseFloat(firstEvent.gcodePosition),
      parseFloat(firstEvent.cncE),
      parseFloat(lastEvent.feederP),
      parseFloat(lastEvent.gcodePosition),
      parseFloat(lastEvent.cncE)
    );
    const yMax = Math.max(
      parseFloat(firstEvent.feederP),
      parseFloat(firstEvent.gcodePosition),
      parseFloat(firstEvent.cncE),
      parseFloat(lastEvent.feederP),
      parseFloat(lastEvent.gcodePosition),
      parseFloat(lastEvent.cncE)
    );
    const newReportInfo: ReportChartInfo = {
      domainXMin: firstIndex,
      domainXMax: lastIndex,
      domainYMin: yMin,
      domainYMax: yMax,
      rows: reportdata?.rows.slice(firstIndex, lastIndex) ?? [],
      step: Math.ceil((lastIndex - firstIndex) / 16),
    };
    setReportChartInfo(newReportInfo);
  };

  let maxDevPercentage = Number.MIN_SAFE_INTEGER;
  let minGCodeOffset = Number.MAX_SAFE_INTEGER;
  let maxGCodeOffset = Number.MIN_SAFE_INTEGER;

  for (const element of feederInfo) {
    if (!element) continue;

    if (element.devPercentage > maxDevPercentage) maxDevPercentage = element.devPercentage;

    if (element.gCodeOffset != null) {
      if (element.gCodeOffset > maxGCodeOffset) maxGCodeOffset = element.gCodeOffset;
      if (element.gCodeOffset < minGCodeOffset) minGCodeOffset = element.gCodeOffset;
    }

    if (element.klipperGCodeOffset != null) {
      if (element.klipperGCodeOffset > maxGCodeOffset) maxGCodeOffset = element.klipperGCodeOffset;
      if (element.klipperGCodeOffset < minGCodeOffset) minGCodeOffset = element.klipperGCodeOffset;
    }
  }

  const handleChangeCurrentEvent = (event: unknown) => {
    if (event == null) return;

    // Recharts passes activeTooltipIndex which can be number, null, or undefined
    const activeTooltipIndex = (event as { activeTooltipIndex?: number | null })
      ?.activeTooltipIndex;

    if (
      activeTooltipIndex == null ||
      activeTooltipIndex < 0 ||
      activeTooltipIndex >= events?.length
    ) {
      return;
    }

    if (typeof onActiveIndexChanged === 'function') {
      onActiveIndexChanged(activeTooltipIndex);
    }
  };

  return (
    <Grid container spacing={3} style={{ marginTop: 20, marginBottom: 20 }}>
      {/* Graph 1: Z Position Over Time */}
      <Grid size={{ xs: 12, md: 6 }} style={{ fontSize: 12 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={feederInfo}
            margin={{ top: 10, bottom: 10, right: 10, left: 10 }}
            onMouseDown={handleChangeCurrentEvent}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis domain={[0, feederInfo.length - 1]} interval={10} dataKey="index">
              <Label value={`Z: ${z}`} offset={0} position="bottom" />
            </XAxis>
            <YAxis unit="mm" name="index" />
            <Tooltip />
            <ReferenceLine x={index} stroke="#4caf50" />
            <Line dot={false} strokeWidth={2} type="linear" dataKey="z" stroke="#2196f3" />
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Graph 2: E Positions (6 lines) */}
      <Grid size={{ xs: 12, md: 6 }} style={{ fontSize: 12 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={feederInfo}
            margin={{ top: 10, bottom: 10, left: 20, right: 10 }}
            onMouseDown={handleChangeCurrentEvent}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              domain={[0, feederInfo.length - 1]}
              interval={10}
              dataKey="index"
              allowDecimals={false}
            >
              <Label
                value={[
                  `Pos E: ${cncE}`,
                  `Feed E: ${feederE}`,
                  `Feed P: ${feederP}`,
                  !isNaN(gcodePosition) ? `GCode P: ${gcodePosition}` : undefined,
                  `CncE vs FeederP: ${cncEvsFeederP}`,
                  !isNaN(difGcodePositionFeederP)
                    ? `GCodePosition vs Feeder P: ${difGcodePositionFeederP}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                offset={0}
                position="bottom"
              />
            </XAxis>
            <YAxis unit="mm" name="E" domain={['dataMin', 'dataMax']} />
            <Tooltip />
            <ReferenceLine x={index} stroke="#4caf50" />
            <Line dot={false} strokeWidth={2} type="linear" dataKey="feederE" stroke="#ff9800" />
            <Line dot={false} strokeWidth={2} type="linear" dataKey="cncE" stroke="#2196f3" />
            <Line dot={false} strokeWidth={2} type="linear" dataKey="feederP" stroke="#0f7a2b" />
            {!isNaN(gcodePosition) && (
              <Line
                dot={false}
                strokeWidth={2}
                type="linear"
                dataKey="gcodePosition"
                stroke="#8b16a8"
              />
            )}
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="cncEvsFeederP"
              stroke="#ff0000"
            />
            {!isNaN(difGcodePositionFeederP) && (
              <Line
                dot={false}
                strokeWidth={2}
                type="linear"
                dataKey="difGcodePositionFeederP"
                stroke="#16cca2"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Graph 3: Deviation Percentage */}
      <Grid size={{ xs: 12, md: 6 }} style={{ fontSize: 12 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={feederInfo}
            margin={{ top: 10, bottom: 10, left: 20, right: 10 }}
            onMouseDown={handleChangeCurrentEvent}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis domain={[0, feederInfo.length - 1]} interval={10} dataKey="index">
              <Label value={`Dev %: ${devPercentage}`} offset={0} position="bottom" />
            </XAxis>
            <YAxis domain={[0, maxDevPercentage]} unit="%" name="devPercentage" />
            <Tooltip />
            <ReferenceLine x={index} stroke="#4caf50" />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="devPercentage"
              stroke="#ff0000"
            />
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Graph 4: Temperature (dual lines) */}
      <Grid size={{ xs: 12, md: 6 }} style={{ fontSize: 12 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            data={feederInfo}
            onMouseDown={handleChangeCurrentEvent}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis domain={[0, feederInfo.length - 1]} interval={10} dataKey="index">
              <Label
                value={[`Temp: ${temperature}º`, `Temp2: ${temperature2}º`].join(' ')}
                offset={0}
                position="bottom"
              />
            </XAxis>
            <YAxis unit="º" domain={[200, 260]} />
            <Tooltip />
            <ReferenceLine x={index} stroke="#4caf50" />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="temperature"
              stroke="#2196f3"
            />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="temperature2"
              stroke="#8b16a8"
            />
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Graph 5: G-Code Offset with mode transitions */}
      <Grid size={{ xs: 12, md: 6 }} style={{ fontSize: 12 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={feederInfo}
            margin={{ top: 10, bottom: 10, right: 10, left: 10 }}
            onMouseDown={handleChangeCurrentEvent}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis interval={9} dataKey="index">
              <Label
                value={`G-Code-Offset Z: ${klipperGCodeOffset ?? gCodeOffset}`}
                offset={0}
                position="bottom"
              />
            </XAxis>
            <YAxis unit="mm" name="index" domain={[minGCodeOffset, maxGCodeOffset]} />
            <Tooltip
              content={(props) => {
                // Recharts Tooltip props structure is different from typed TooltipProps
                const payload = (
                  props as unknown as { payload?: Array<{ payload: FeederInfo; color?: string }> }
                )?.payload;
                if (!payload?.length) return null;
                const record = payload[0].payload;
                return (
                  <StyledCard>
                    <CardContent>
                      {record.index}
                      <br />
                      <span
                        style={{
                          color: payload[payload.length - 1].color,
                          marginTop: '5px',
                        }}
                      >
                        G-CODE-OFFSET: {record.klipperGCodeOffset ?? record.gCodeOffset}
                      </span>
                    </CardContent>
                  </StyledCard>
                );
              }}
            />
            <ReferenceLine x={index} stroke="#4caf50" />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="gCodeOffsetManualToAuto"
              stroke="#ff0000"
            />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="gCodeOffsetAutoToManual"
              stroke="#f3bf21"
            />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="klipperGCodeOffset"
              stroke="#ff0000"
            />
            <Line
              dot={false}
              strokeWidth={2}
              type="linear"
              dataKey="gCodeOffset"
              stroke="#f3bf21"
            />
          </LineChart>
        </ResponsiveContainer>
      </Grid>

      {/* Graph 6: Report Data (conditional on reportUrl) */}
      {reportdata && reportdata.rows && reportdata.rows.length > 0 && (
        <Grid size={12} style={{ fontSize: 12 }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
              data={reportChartInfo.rows}
              onMouseDown={mouseDownHandler}
              onMouseUp={mouseUpHandler}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                domain={[reportChartInfo.domainXMin, reportChartInfo.domainXMax]}
                interval={100}
                dataKey="index"
              />
              <YAxis unit="mm" domain={[reportChartInfo.domainYMin, reportChartInfo.domainYMax]} />
              <Tooltip />
              <ReferenceLine x={index} stroke="#4caf50" />
              <Line dot={false} strokeWidth={2} type="linear" dataKey="cncE" stroke="#FF0000" />
              <Line
                dot={false}
                strokeWidth={2}
                type="linear"
                dataKey="gcodePosition"
                stroke="#00FF00"
              />
              <Line dot={false} strokeWidth={2} type="linear" dataKey="feederP" stroke="#0000FF" />
            </LineChart>
          </ResponsiveContainer>
        </Grid>
      )}
    </Grid>
  );
};
